import { Injectable, Inject, Optional } from '@angular/core';
import { Subject } from 'rxjs';
import { Enum } from '../../common/interfaces/enum.interface';
import { ROLES_TYPE } from '../constants/consts';
import { UiHelperService } from '../../ui-helper/services/ui-helper.service';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { InitializerService } from '../../initializer/initializer.service';

/// singleton class per all app
@Injectable()
export class CoreAuthService {
    onAuthenticationChangeSubject: Subject<boolean> = new Subject();
    private isDirty = false;
    private isUserLoggedIn = false;
    private rolesLoggedIn: string[];
    private isAuthenticatedByServer = false;
    private rolesToken: string[];

    constructor(
        private initializerService: InitializerService,
        @Inject(ROLES_TYPE) private rolesType: Enum,
        private uiHelper: UiHelperService,
        private transferState: TransferState
    ) {
        const isAuthStateKey = makeStateKey<boolean>('IsUserAuthenticated');
        const rolesKey = makeStateKey<string[]>('Roles');

        if (!this.uiHelper.isBrowser) {
            this.isAuthenticatedByServer = this.initializerService.getClientContext().IsAuthenticated;
            this.rolesToken = this.initializerService.getClientContext().Roles;
            this.transferState.set(isAuthStateKey, this.isAuthenticatedByServer);
            this.transferState.set(rolesKey, this.rolesToken);
        }
        else {
            this.isAuthenticatedByServer = this.transferState.get(isAuthStateKey, false);
            this.rolesToken = this.transferState.get(rolesKey, []);
        }
    }

    isUserAuthenticated(): boolean {
        return this.isDirty ? this.isUserLoggedIn : this.isAuthenticatedByServer;
    }

    // if you made any changes to this function do the same for isUserInRoleName function.
    isUserInRole(role: number): boolean {
        return this.isDirty && this.rolesLoggedIn !== undefined
            ? this.rolesLoggedIn.includes(this.rolesType[role])
            : this.rolesToken !== undefined
                ? this.rolesToken.includes(this.rolesType[role])
                : false;
    }

    isUserMatchRoles(userRoles: number[]): boolean {
        for (const userRole of userRoles) {
            if (this.isUserInRole(userRole)) {
                return true;
            }
        }
        return false;
    }

    // if you made any changes to this function do the same for isUserInRole function.
    isUserInRoleName(role: string): boolean {
        return this.isDirty && this.rolesLoggedIn !== undefined
            ? this.rolesLoggedIn.includes(role)
            : this.rolesToken !== undefined
                ? this.rolesToken.includes(role)
                : false;
    }

    isUserMatchRolesNames(rolesNames: string[]): boolean {
        for (const role of rolesNames) {
            if (this.isUserInRoleName(role)) {
                return true;
            }
        }
        return false;
    }


    markUserHasAuthenticated(rolesArray: string[]): void {
        this.isDirty = true;
        this.isUserLoggedIn = true;
        this.rolesLoggedIn = rolesArray;
        this.onAuthenticationChangeSubject.next(true);
    }

    markUserHasAnonymous(): void {
        this.isDirty = true;
        this.isUserLoggedIn = false;
        this.rolesLoggedIn = undefined;
        this.rolesToken = undefined;
        this.onAuthenticationChangeSubject.next(false);
    }

    authenticate(roles: string[]) {
        this.markUserHasAuthenticated(roles);
    }

    anonymous() {
        this.markUserHasAnonymous();
    }
}
